/** Cerrar torneo (Eduardo 2026-09-27): textos del Resumen, del comunicado
 * de campeones y de la bitácora. Funciones puras para poder probarlas. */

export interface FinalResultLike {
  division_name: string;
  name: string;
  position: number | null;
  label: string;
  podium?: "champion" | "runner_up" | "third" | null;
}

/** Campeón/subcampeón SOLO por significado (el backend manda `podium`;
 * respaldo: el rótulo). «1º Grupo A» es primero de grupo, no campeón. */
function podiumOf(row: FinalResultLike): "champion" | "runner_up" | "third" | null {
  if (row.podium !== undefined) return row.podium ?? null;
  if (row.label === "Campeón") return "champion";
  if (row.label === "Subcampeón") return "runner_up";
  if (row.label === "3er lugar") return "third";
  return null;
}

export interface ClosePreviewLike {
  /** Total real: todo lo que queda «No jugado» al cerrar. */
  pending_matches_total: number;
  pending_matches: Array<{ division_name: string; count: number }>;
  /** Eduardo 2026-10-05: lo que ya tocaba jugar (jornadas hasta la actual,
   * con los dos lados definidos). Opcionales: un servidor viejo no los manda. */
  due_matches_total?: number;
  due_matches?: Array<{ division_name: string; count: number }>;
  /** Jornadas que faltan y partidos aún sin rival (también quedan «No jugado»). */
  later_matches_total?: number;
  later_rounds?: number;
  pending_reports: number;
  pending_orders: number;
  finals_without_winner: string[];
  /** Finales sin ganador que ya tienen a sus dos finalistas. */
  due_finals_without_winner?: string[];
}

export interface LifecycleEventLike {
  action: string;
  user_name: string | null;
  reason: string | null;
  notify_players: boolean | null;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

function pendingLines(
  preview: ClosePreviewLike,
  matchesTotal: number,
  matchesByDivision: Array<{ division_name: string; count: number }>,
  finalsWithoutWinner: string[],
): string[] {
  const lines: string[] = [];
  if (matchesTotal > 0) {
    const detail = matchesByDivision
      .map((row) => `${row.division_name}: ${row.count}`)
      .join(", ");
    lines.push(
      `${plural(matchesTotal, "partido sin resultado", "partidos sin resultado")}${detail ? ` (${detail})` : ""}: quedarán como «No jugado».`,
    );
  }
  if (finalsWithoutWinner.length > 0) {
    lines.push(`Final sin ganador en ${finalsWithoutWinner.join(", ")}: no habrá campeón en esa categoría.`);
  }
  if (preview.pending_reports > 0) {
    lines.push(
      `${plural(preview.pending_reports, "reporte de jugador", "reportes de jugadores")} sin revisar: ${preview.pending_reports === 1 ? "se descarta" : "se descartan"}.`,
    );
  }
  if (preview.pending_orders > 0) {
    lines.push(
      `${plural(preview.pending_orders, "pago pendiente", "pagos pendientes")}: podrás seguir cobrándolos con el torneo cerrado.`,
    );
  }
  return lines;
}

/** «Pendiente» del Resumen, en frases cortas (vacío = nada pendiente).
 * Eduardo 2026-10-05: solo los partidos que ya tocaba jugar (la jornada
 * actual y las que ya pasaron), no los de jornadas futuras. */
export function closePendingLines(preview: ClosePreviewLike): string[] {
  return pendingLines(
    preview,
    preview.due_matches_total ?? preview.pending_matches_total,
    preview.due_matches ?? preview.pending_matches,
    preview.due_finals_without_winner ?? preview.finals_without_winner,
  );
}

/** Lo que falta por jugar (fuera de «Pendiente»), sin alarmar: cerrar ahora
 * también lo deja «No jugado». null = nada por delante. */
export function closeLaterLine(preview: ClosePreviewLike): string | null {
  const later = preview.later_matches_total ?? 0;
  if (later <= 0) return null;
  const rounds = preview.later_rounds ?? 0;
  const what =
    rounds > 0
      ? `${rounds === 1 ? "Falta" : "Faltan"} ${plural(rounds, "jornada", "jornadas")} por jugar (${plural(later, "partido", "partidos")}).`
      : `${later === 1 ? "Falta" : "Faltan"} ${plural(later, "partido", "partidos")} por jugar de las rondas siguientes.`;
  return `${what} Si cierras ahora, ${later === 1 ? "quedará" : "quedarán"} como «No jugado».`;
}

/** Texto de la confirmación de cierre: con el total REAL de partidos que
 * quedan «No jugado» (también los de jornadas futuras). */
export function closeConfirmMessage(tournamentName: string, preview: ClosePreviewLike | null): string {
  const pending = preview
    ? pendingLines(preview, preview.pending_matches_total, preview.pending_matches, preview.finals_without_winner)
    : [];
  return (
    `¿Cerrar «${tournamentName}»?\n\n` +
    "Queda en solo lectura: resultados, grupos, draw, calendario, inscripciones y ranking ya no cambian. " +
    "Podrás reabrirlo con un motivo." +
    (pending.length ? `\n\nPendiente:\n• ${pending.join("\n• ")}` : "")
  );
}

export interface DivisionPodium {
  division_name: string;
  champion: string | null;
  runner_up: string | null;
  /** Sin campeón (varios grupos sin final): los primeros de cada grupo. */
  group_leaders: string[];
}

/** Podio por categoría, en el orden de las categorías. */
export function podiumByDivision(results: FinalResultLike[]): DivisionPodium[] {
  const order: string[] = [];
  const byDivision = new Map<string, DivisionPodium>();
  for (const row of results) {
    if (!byDivision.has(row.division_name)) {
      order.push(row.division_name);
      byDivision.set(row.division_name, { division_name: row.division_name, champion: null, runner_up: null, group_leaders: [] });
    }
    const entry = byDivision.get(row.division_name)!;
    const podium = podiumOf(row);
    if (podium === "champion" && !entry.champion) entry.champion = row.name;
    // Final en Doble W.O.: no hay campeón y los dos son subcampeones.
    else if (podium === "runner_up") entry.runner_up = entry.runner_up ? `${entry.runner_up} y ${row.name}` : row.name;
    else if (!podium && row.position === 1) entry.group_leaders.push(`${row.name} (${row.label})`);
  }
  return order
    .map((division_name) => byDivision.get(division_name)!)
    .map((entry) => (entry.champion ? { ...entry, group_leaders: [] } : entry))
    .filter((entry) => entry.champion || entry.runner_up || entry.group_leaders.length > 0);
}

/** Borrador del comunicado de campeones (el admin lo revisa y decide si lo manda). */
export function championsAnnouncement(tournamentName: string, results: FinalResultLike[]): { title: string; body: string } {
  const podium = podiumByDivision(results);
  const lines = podium.map((row) => {
    const parts = row.champion || row.runner_up
      ? [row.champion ? `Campeón: ${row.champion}` : null, row.runner_up ? `Subcampeón: ${row.runner_up}` : null]
          .filter(Boolean)
          .join(" · ")
      : `Primeros de grupo: ${row.group_leaders.join(", ")}`;
    return `• ${row.division_name} — ${parts}`;
  });
  return {
    title: `Resultados finales — ${tournamentName}`,
    body:
      `¡Gracias por jugar ${tournamentName}!\n\n` +
      (lines.length ? `Campeones:\n${lines.join("\n")}\n\n` : "") +
      "Felicidades a todos los participantes. Nos vemos en el próximo torneo.",
  };
}

/** Renglón de la bitácora del cierre. */
export function lifecycleEventText(event: LifecycleEventLike): string {
  const who = event.user_name ? ` por ${event.user_name}` : "";
  switch (event.action) {
    case "closed":
      return `Cerrado${who}.`;
    case "reopened":
      return `Reabierto${who}: «${event.reason ?? ""}»${event.notify_players ? " (avisando a los jugadores)" : " (sin avisar a los jugadores)"}.`;
    case "superadmin_edit":
      return `Cambio del superadmin${who} con el torneo cerrado.`;
    case "migrated_from_archived":
      return "Estaba archivado: pasó a Cerrado.";
    default:
      return event.action;
  }
}
